'use client'

/**
 * Listing-detail broker CTA. ONE card, ONE style, ONE location — the
 * TextMattCTA "Talk to a broker / Questions about this home?" card. The only
 * thing that changes is WHOSE contact it shows:
 *
 *   - Lead has been assigned to a broker (arrived via that broker's CRM link or
 *     ad → rr_agent_attribution cookie): the card shows THAT broker's contact.
 *   - No assignment: the card shows the default principal broker.
 *
 * It never says "your broker" and never changes shape based on assignment, so a
 * visitor cannot tell from the layout whether they have been attributed. The
 * cookie is read client-side so static / ISR listing pages stay static; the card
 * SSRs with the default broker and swaps in place after hydration if attributed.
 *
 * THE STICKY BAR IS DROPPED (Matt / Critiquito 2026-09-19). Call / Text stay
 * on this card (the agent flow). Tour is the primary beside the price.
 * Work with us lives in the site header.
 */
import { TextMattCTA } from './TextMattCTA'
import { useAttributedBroker } from './use-attributed-broker'
import type { Broker } from '@/lib/data/types/broker'
import type { ReviewsSummary } from '@/lib/data/reviews/getReviews'

export default function ListingBrokerCTA({
  defaultBroker,
  brokers,
  listingKey,
  reviews,
  className,
  lockToDefault = false,
  offMarket = null,
}: {
  defaultBroker: Broker
  brokers: Broker[]
  listingKey: string
  /** Brokerage Google reviews for the desktop card's lg-only social-proof block. */
  reviews?: ReviewsSummary | null
  className?: string
  /** True when defaultBroker is the resolved Ryan Realty listing agent for THIS
   *  home — keep them as the contact; never random-reassign over them. */
  lockToDefault?: boolean
  /**
   * SITE-21: the home is not for sale, so the card's ask is not a showing.
   * 'sold' and 'unsold' ask different questions — see TextMattCTA.
   */
  offMarket?: 'sold' | 'unsold' | null
}) {
  // assign: true — the card owns the sticky assignment for an unattributed
  // visitor, and the bar reads the cookie it writes.
  const broker = useAttributedBroker({ defaultBroker, brokers, lockToDefault, assign: true })

  return (
    <TextMattCTA
      broker={broker}
      listingKey={listingKey}
      reviews={reviews}
      offMarket={offMarket}
      className={['listing-broker-card', className].filter(Boolean).join(' ')}
    />
  )
}
